<template>
    <div v-if="event" class="exercise-item"
        :class="{ 'is-hover-view': isHoverView, 'is-dragging': dragging, 'student-edited': isEdited }">
        <div class="exercise-accent" :style="`background-color: ${event.event.color}`" />
        <button-tooltip v-if="isEdited" variant="rich" tap-to-open :interactive="hasVideo" :disabled="dragging">
            <template v-slot:activator="{ on, attrs }">
                <span class="ex-mod-dot-sm" v-on="on" v-bind="attrs" />
            </template>
            <template v-slot:tooltip>
                <exercise-change-boxes :changes="changes" @click:video="$emit('click:video', $event)" />
            </template>
        </button-tooltip>
        <div class="exercise-name">
            {{ ghostLabel || (ghostCircuitCount > 0 ? $t('circuits.circuit') : event.event.name) }}
        </div>
        <div class="exercise-sets">{{ infoLabel }}</div>
        <div v-if="canQuickEdit" class="exercise-quick" @mousedown.stop @mouseup.stop @click.stop>
            <button-tooltip :tooltip="$t('tooltips.set_remove')">
                <template v-slot:activator="{ on, attrs }">
                    <span class="quick-btn" v-on="on" v-bind="attrs" @click="$emit('click:set:remove')">&#9660;</span>
                </template>
            </button-tooltip>
            <span class="quick-val">{{ seriesCount }}</span>
            <button-tooltip :tooltip="$t('tooltips.set_add')">
                <template v-slot:activator="{ on, attrs }">
                    <span class="quick-btn" v-on="on" v-bind="attrs" @click="$emit('click:set:add')">&#9650;</span>
                </template>
            </button-tooltip>
            <span class="quick-sep">×</span>
            <button-tooltip :tooltip="$t('tooltips.value_remove')">
                <template v-slot:activator="{ on, attrs }">
                    <span class="quick-btn" v-on="on" v-bind="attrs" @click="$emit('click:reps', -1)">&#9660;</span>
                </template>
            </button-tooltip>
            <span class="quick-val">{{ repsValue }}</span>
            <button-tooltip :tooltip="$t('tooltips.value_add')">
                <template v-slot:activator="{ on, attrs }">
                    <span class="quick-btn" v-on="on" v-bind="attrs" @click="$emit('click:reps', 1)">&#9650;</span>
                </template>
            </button-tooltip>
            <span class="quick-unit">{{ repsUnit }}</span>
        </div>
    </div>
</template>

<script>
import ButtonTooltip from "@/components/common/ButtonTooltip";
import ExerciseChangeBoxes from "@/components/common/ExerciseChangeBoxes";
export default {
    name: "ExerciseEvent",
    components: { ButtonTooltip, ExerciseChangeBoxes },
    props: {
        event: {
            type: Object,
            default: null,
        },
        changes: {
            type: Array,
            default: null,
        },
        isHoverView: {
            type: Boolean,
            default: false,
        },
        ghostCircuitCount: {
            type: Number,
            default: 0,
        },
        ghostLabel: {
            type: String,
            default: null,
        },
        dragging: {
            type: Boolean,
            default: false,
        }
    },
    computed: {
        isEdited(vm = this) {
            return !!vm.changes?.length
        },
        hasVideo(vm = this) {
            return (vm.changes || []).some(change => change.type === 'video' && !change.expired)
        },
        infoLabel(vm = this) {
            if (vm.ghostCircuitCount > 0) return `${vm.ghostCircuitCount} ${vm.$tc('exercises.exercises', vm.ghostCircuitCount)}`
            return vm.isCircuit ? vm.$t('circuits.circuit') : vm.exercisesSets
        },
        isCircuit(vm = this) {
            return vm.event.event.isCircuit || false
        },
        isTabata(vm = this) {
            return vm.event.event.isTabata || false
        },
        quickConfig(vm = this) {
            return vm.event?.event?.config || null
        },
        seriesCount(vm = this) {
            return vm.quickConfig?.sets?.length || 0
        },
        repsValue(vm = this) {
            return vm.quickConfig?.sets?.[0]?.data?.[0]?.value
        },
        repsUnit(vm = this) {
            const shortName = vm.quickConfig?.sets?.[0]?.data?.[0]?.short_name
            return shortName ? vm.$t(`exercises.${shortName}`) : ''
        },
        canQuickEdit(vm = this) {
            return !vm.isHoverView && !vm.isCircuit && !vm.isTabata && !vm.dragging
                && vm.can('quickEdit')
                && vm.quickConfig?.sets?.length > 0
                && vm.quickConfig.sets[0]?.data?.length > 0
                && vm.repsValue != null
        },
        exercisesSets(vm = this) {
            if (vm.isTabata) return ''

            if (vm.isCircuit) {
                let quantitySets = vm.event.event.sets
                let value = vm.event.event.config.value
                let type = vm.$t(`exercises.short_${vm.event.event.config.type}`)
                return `${quantitySets}x${value}${type}`
            }

            let config = vm.event.event.sets || vm.event.event.config || null
            if (!config || !config.sets?.length || !config.sets[0]?.data?.length) return vm.$t('common.not_available_short')
            let quantitySets = config.sets.length
            let type = vm.$t(`exercises.${config.sets[0].data[0].short_name}`)

            let result = ''
            if (quantitySets === 1) {
                result = `${quantitySets}x${config.sets[0].data[0].value}${type}`
            } else {
                let values = []
                let current = null
                let sameValue = true
                config.sets.forEach(set => {
                    if (!current) current = set.data[0].value
                    values.push(set.data[0].value)
                    if (current !== set.data[0].value) sameValue = false
                    current = set.data[0].value
                })

                if (sameValue) {
                    result = `${quantitySets}x${values[0]}${type}`
                } else {
                    result = `${quantitySets}x${values.join(`${type},`)}${type}`
                }
            }
            return result
        },
    }
}
</script>

<style scoped>
.exercise-item {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 100%;
    min-height: 22px;
    position: relative;
    padding: 0 8px 0 6px;
    border-radius: 4px;
    cursor: grab;
    user-select: none;
}

.exercise-item:not(.is-hover-view):not(.is-dragging):hover {
    background: var(--v-surface2-base);
}

.exercise-item.student-edited {
    background: rgba(232, 92, 92, .08);
}

.exercise-item.student-edited:not(.is-hover-view):not(.is-dragging):hover {
    background: rgba(232, 92, 92, .14);
}

.ex-mod-dot-sm {
    position: relative;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--v-error-base);
    box-shadow: 0 0 0 2px rgba(232, 92, 92, .18);
    flex-shrink: 0;
    cursor: pointer;
}

.ex-mod-dot-sm::after {
    content: '';
    position: absolute;
    inset: -5px -7px;
}

.exercise-accent {
    width: 2px;
    height: 14px;
    border-radius: 1px;
    flex-shrink: 0;
}

.exercise-name {
    font-size: 11px;
    font-weight: 400;
    color: var(--v-txtPrimary-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
}

.exercise-sets {
    font-size: 10px;
    font-weight: 400;
    color: var(--v-txtMuted-base);
    white-space: nowrap;
    flex-shrink: 0;
    transition: opacity .12s;
}

.exercise-item:not(.is-hover-view):not(.is-dragging):hover .exercise-sets {
    opacity: 0;
}

.exercise-quick {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    gap: 2px;
    opacity: 0;
    transition: opacity .12s;
    pointer-events: none;
    background: var(--v-surface2-base);
    padding: 0 2px 0 6px;
    border-radius: 4px;
}

.exercise-item:hover .exercise-quick {
    opacity: 1;
}

/* solo los botones capturan el mouse: el resto de la fila sigue siendo arrastrable */
.exercise-item:hover .exercise-quick .quick-btn {
    pointer-events: auto;
}

.quick-btn {
    width: 15px;
    height: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 3px;
    cursor: pointer;
    font-size: 7px;
    color: var(--v-txtSecondary-base);
    user-select: none;
    line-height: 1;
}

.quick-btn:hover {
    background: var(--v-accent-base);
    color: #fff;
    border-color: var(--v-accent-base);
}

.quick-val {
    font-size: 10px;
    font-weight: 800;
    color: var(--v-txtPrimary-base);
    min-width: 12px;
    text-align: center;
}

.quick-sep {
    font-size: 9px;
    color: var(--v-txtMuted-base);
    margin: 0 1px;
}

.quick-unit {
    font-size: 9px;
    color: var(--v-txtMuted-base);
    margin-left: 1px;
}

/* Compact list row (program/block detail) */
.exercise-item.is-hover-view {
    height: auto;
    padding: 7px 10px;
    cursor: default;
    border-bottom: 1px solid var(--v-outline-base);
}

.exercise-item.is-hover-view .exercise-name {
    font-weight: 500;
}
</style>
